import { forkJoin } from 'rxjs';
import { TeamModel } from 'projects/core/src/lib/models/team.model';
import { EmployeeModel } from 'projects/core/src/lib/models/employee.model';
import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { MIN_PASSWORD_LENGTH } from 'projects/admin/src/app/shared/constants';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { RoleModel } from 'projects/core/src/lib/models/role.model';
import { SelectItem } from 'primeng/api';
import { LANGUAGES, SALUTATIONS } from 'projects/core/src/lib/constants';
import { EmployeeSearchComponent } from 'projects/admin/src/app/shared/employee-search/employee-search.component';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';

@Component({
  selector: 'app-user-new',
  templateUrl: './user-new.component.html',
  styleUrls: ['./user-new.component.scss']
})
export class UserNewComponent extends BaseForm implements OnInit {

  @ViewChild('employeesearch') employeesearch: EmployeeSearchComponent;

  public roles: Array<RoleModel> = [];
  public country: string | null = null;
  public timezone: string | null = null;
  public salutations: Array<SelectItem> = [];
  public languages: Array<SelectItem> = [];
  public teams: Array<TeamModel> = [];

  public form = this.fb.group({
    gender: new UntypedFormControl('', [Validators.required]),
    firstname: new UntypedFormControl('', [Validators.required]),
    lastname: new UntypedFormControl('', [Validators.required]),
    roles: new UntypedFormControl('', [Validators.required]),
    email: new UntypedFormControl('', [Validators.required, CustomValidators.email()]),
    phone: new UntypedFormControl(''),
    mobile: new UntypedFormControl(''),
    team: new UntypedFormControl(''),
    company: new UntypedFormControl(''),
    language: new UntypedFormControl('', [Validators.required]),
    comment: new UntypedFormControl(''),
    passwords: this.fb.group({
      password: new UntypedFormControl(''),
      confirm: new UntypedFormControl(''),
    }, {
      validators: [this.passwordValidator]
    }),
    tz: this.fb.group({}),
  });

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private router: Router,
    private auth: AuthStateService,
    private transloco: TranslocoService
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.auth.checkRightOrRedirect('admin.user.create')) {
      this.salutations = FormHelper.getSelectList(SALUTATIONS, null, this.transloco);
      this.languages = FormHelper.getSelectList(LANGUAGES, null, this.transloco);
      this.loading = true;
      forkJoin([
        this.api.getModels('roles'),
        this.api.getModels('teams'),
      ]).subscribe({
        next: (result: any) => {
          if (result[0] !== null && result[0].data.length > 0) {
            result[0].data.forEach((role: any) => {
              role = new RoleModel().deserialize(role);
              role.setTranslocoService(this.transloco);
              this.roles.push(role);
            });
          }
          if (result[1] !== null && result[1].data.length > 0) {
            result[1].data.forEach((team: any) => {
              this.teams.push(new TeamModel().deserialize(team));
            });
          }
          this.loading = false;
          this.ui.setFormFocus();
        }
      });
    }
  }

  onCancel(): void {
    this.router.navigate(['/users']);
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload: any = {
        id: null,
        gender: this.form.value.gender,
        firstname: this.form.value.firstname,
        lastname: this.form.value.lastname,
        email: this.form.value.email,
        phone: this.form.value.phone,
        mobile: this.form.value.mobile,
        team: this.form.value.team?.id,
        language: this.form.value.language,
        company: this.form.value.company,
        comment: this.form.value.comment,
        password: this.form.value.passwords.password,
        country: this.country,
        timezone: this.timezone,
        misc: this.form.value.misc,
      };
      const roles = [];
      this.form.value?.roles?.forEach((role: RoleModel) => {
        roles.push(role.id);
      });
      payload.roles = roles;
      this.api.saveModel('users', null, payload).subscribe({
        next: (result: any) => {
          this.router.navigate(['/users', result.data.id]);
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.form.value.firstname + ' ' + this.form.value.lastname }), this.transloco.translate('common.saved'));
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  private hasRole(rolename: string): boolean {
    const roles = this.form.get('roles').value;
    return (Array.isArray(roles)) && roles.find((role: RoleModel) => {
      return role.name === rolename;
    });
  }

  public onOpenEmployeeSearch(): void {
    this.employeesearch.open();
  }

  public onSelectEmployee(employee: EmployeeModel): void {
    this.form.patchValue({
      gender: this.salutations.find(salutation => salutation.value[0] === employee.gender)?.value,
      firstname: employee.firstname,
      lastname: employee.lastname,
      email: employee.email,
      phone: employee.phone,
      mobile: employee.mobile,
    });
  }

  private passwordValidator(group: UntypedFormGroup): void {
    const password = group.get('password')?.value;
    const password2 = group.get('confirm')?.value;
    group.get('password').setErrors(null);
    group.get('confirm').setErrors(null);
    if (!password) {
      // group.get('password')?.setErrors({ required: true });
    } else {
      const errors = [];
      if (password.length < MIN_PASSWORD_LENGTH) {
        errors.push('passwordLength');
      }
      if (!(/\d/.test(password) && /[A-Z]/.test(password) && /[a-z]/.test(password))) {
        errors.push('passwordStrength');
      }
      if (errors.length > 0) {
        const errorList = {};
        errors.forEach((error: string) => {
          errorList[error] = true;
        });
        group.get('password')?.setErrors(errorList);
      } else {
        group.get('password')?.setErrors(null);
      }
      if (!password2) {
        group.get('confirm')?.setErrors({ required: true });
      } else if (password !== password2) {
        group.get('confirm')?.setErrors({ passwordMatch: true });
      }
    }
  }
}
